import { parseDecklistText, type DecklistCardInput } from 'store/services/cards/decklistApi';
import { COMMANDER_DECK_SIZE, COMMANDER_FAMILY_FORMATS, MAX_SIDEBOARD, MIN_MAIN_CONSTRUCTED, OTHER_FORMAT_LABEL } from 'constants/play';
import type { DecklistIssue } from 'types/interfaces/play';

export interface DecklistSections {
  main: DecklistCardInput[];
  side: DecklistCardInput[];
  mainCount: number;
  sideCount: number;
}

const SIDEBOARD_LINE = /^\s*sideboard\b/i;
/** Arena export suffix: "4 Lightning Bolt (M21) 199" */
const ARENA_SUFFIX = /\s+\([A-Z0-9]{2,6}\)\s+[A-Za-z0-9-]+\s*$/;

const count = (cards: DecklistCardInput[]): number => cards.reduce((sum, c) => sum + (c.quantity ?? 1), 0);

/**
 * Decision 18: MTGO/Arena text where a "Sideboard" line separates the sections.
 * The shared parser (store/services/cards/decklistApi.ts) SKIPS that line and
 * never splits, so this wrapper splits first, strips Arena set suffixes the
 * shared parser would keep in the name, then parses each half. Used only for
 * the live counts hint; the server is the validator of record.
 */
export const parseDecklistSections = (text: string): DecklistSections => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const idx = lines.findIndex((l) => SIDEBOARD_LINE.test(l));
  const mainLines = idx === -1 ? lines : lines.slice(0, idx);
  const sideLines = idx === -1 ? [] : lines.slice(idx + 1);
  const clean = (ls: string[]) => ls.map((l) => l.replace(ARENA_SUFFIX, '')).join('\n');
  const main = parseDecklistText(clean(mainLines));
  const side = parseDecklistText(clean(sideLines));
  return { main, side, mainCount: count(main), sideCount: count(side) };
};

/** Count validation applies to 60-card constructed formats only (Decision 18, ruling E11). */
export const countRuleApplies = (formatLabel: string): boolean => formatLabel !== OTHER_FORMAT_LABEL && !COMMANDER_FAMILY_FORMATS.includes(formatLabel);

/** The deck-size rule for the intro line, pulled from the format (decklist review, item 2). */
export const deckRuleSentence = (formatLabel: string): string => {
  if (COMMANDER_FAMILY_FORMATS.includes(formatLabel)) return `${COMMANDER_DECK_SIZE} card singleton, commander included.`;
  if (formatLabel === OTHER_FORMAT_LABEL) return 'No deck size rule for this format.';
  return `${MIN_MAIN_CONSTRUCTED} card main, up to ${MAX_SIDEBOARD} sideboard.`;
};

export const decklistIssues = (sections: Pick<DecklistSections, 'mainCount' | 'sideCount'>, formatLabel: string): DecklistIssue[] => {
  if (!countRuleApplies(formatLabel)) return [];
  const issues: DecklistIssue[] = [];
  if (sections.mainCount < MIN_MAIN_CONSTRUCTED) issues.push('MAIN_UNDER_60');
  if (sections.sideCount > MAX_SIDEBOARD) issues.push('SIDE_OVER_15');
  return issues;
};

export const issueText = (issue: DecklistIssue, sections: Pick<DecklistSections, 'mainCount' | 'sideCount'>): string =>
  issue === 'MAIN_UNDER_60' ? `${MIN_MAIN_CONSTRUCTED - sections.mainCount} short of ${MIN_MAIN_CONSTRUCTED}` : `Sideboard over ${MAX_SIDEBOARD}`;
